import { Asset } from '@/api/AssetApi';
import { UserDataProp } from '../types';
import { ConnectingSlot, ConnectingSlotData } from './ConnectingSlot';
import { CreatorAction, UUID } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { RenderUtils } from '../utils/RenderUtils';
import {
	Vector3Tuple,
	EulerTuple,
	Group,
	Object3D,
	Box3,
	Quaternion,
	Euler,
	Vector3,
} from 'three';

export interface SerializedSofaModule {
	id: UUID;
	objectData: {
		position: Vector3Tuple;
		rotation: EulerTuple;
	};
	materialActions: Record<string, CreatorAction>;
	connectingSlots: ConnectingSlotData[];
	asset: Asset;
}

export class SofaModule {
	id: UUID;
	group = new Group();
	connectingSlots: ConnectingSlot[] = [];
	materialActions: Record<string, CreatorAction> = {};
	private modelObject?: Object3D;
	private renderer: ProjectRenderer;
	private asset: Asset;
	private _selected = false;
	private _hovered = false;

	constructor(
		renderer: ProjectRenderer,
		asset: Asset,
		id: UUID,
		object: Object3D
	) {
		this.renderer = renderer;

		this.id = id;
		this.asset = structuredClone(asset);

		this.group.userData = {
			[UserDataProp.IS_SOFA_MODULE]: true,
			[UserDataProp.SOFA_MODULE_ID]: id,
		};

		this.addModelToGroup(object);
	}

	private addModelToGroup(object: Object3D) {
		this.modelObject = object;
		this.group.add(object);
		this.group.traverse((obj) => {
			obj.layers.disable(0);
			obj.layers.enable(RENDERER_CONFIG.SELECTABLE_LAYER);
			// obj.receiveShadow = true;
			// obj.castShadow = true;
		});

		const box = new Box3().setFromObject(this.group);
		const minY = box.min.y;
		this.group.position.y += -minY;
	}

	get selected() {
		return this._selected;
	}

	set selected(value: boolean) {
		this._selected = value;

		if (value)
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.group.children;
		else
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.renderer.postprocessing.outlinePass.selectedObjects.filter(
					(obj) => {
						const group = RenderUtils.searchSofaModuleGroup(obj);
						if (!group) return true;

						return group.userData[UserDataProp.SOFA_MODULE_ID] !== this.id;
					}
				);
	}

	get hovered() {
		return this._hovered;
	}

	set hovered(value: boolean) {
		if (this.selected) return;

		this._hovered = value;

		if (value)
			this.renderer.postprocessing.outlinePass.selectedObjects.push(
				...this.group.children
			);
		else
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.renderer.postprocessing.outlinePass.selectedObjects.filter(
					(obj) => {
						const group = RenderUtils.searchSofaModuleGroup(obj);
						if (!group) return true;

						const selectedModule = this.renderer.getSelectedModule();
						if (
							selectedModule?.id === group.userData[UserDataProp.SOFA_MODULE_ID]
						)
							return true;

						return group.userData[UserDataProp.SOFA_MODULE_ID] !== this.id;
					}
				);
	}

	async createConnectingSlots(data?: SerializedSofaModule) {
		if (data) {
			for (const slotData of data.connectingSlots) {
				const slot = new ConnectingSlot(this.renderer, this, slotData.slotType);
				await slot.setFromData(slotData);
				this.connectingSlots.push(slot);
			}
			return;
		}

		this.group.traverse((object) => {
			if (!object.userData[UserDataProp.IS_CONNECTING_SLOT]) return;

			const slotType = object.userData[UserDataProp.SLOT_TYPE];
			if (!slotType) throw new Error('MISSING_SLOT_TYPE');

			this.connectingSlots.push(
				new ConnectingSlot(this.renderer, this, slotType, object)
			);
		});
	}

	getOccupiedSlotsData() {
		const occupiedSlots = this.connectingSlots.filter(
			(slot) => !!slot.connectedSlot
		);

		const slotTypes = occupiedSlots.map((slot) => slot.slotType);
		const modulesIds = occupiedSlots
			.map((slot) => slot.connectedSlot?.hostModule.id!)
			.filter((id) => id !== this.id);

		return {
			slotTypes,
			modulesIds,
		};
	}

	connectBySlot() {
		const selectedSlot = this.renderer.selectedConnectingSlot;
		if (!selectedSlot) return;

		const matchSlot = this.connectingSlots.find((s) =>
			s.canConnectTo(selectedSlot)
		);

		if (!matchSlot) return;

		selectedSlot.connectTo(matchSlot);
		this.alignToSlot(selectedSlot, matchSlot);
	}

	alignToSlot(anchorSlot: ConnectingSlot, movingSlot: ConnectingSlot) {
		if (movingSlot.hostModule !== this) return;

		const targetRotation = new Quaternion();
		anchorSlot.point.getWorldQuaternion(targetRotation);

		const movingRotation = new Quaternion();
		movingSlot.point.getWorldQuaternion(movingRotation);

		const targetEuler = new Euler().setFromQuaternion(targetRotation, 'YXZ');
		const movingEuler = new Euler().setFromQuaternion(movingRotation, 'YXZ');

		const angleDiffY = targetEuler.y - movingEuler.y;

		this.group.rotateOnAxis(new Vector3(0, 1, 0), angleDiffY);

		const anchorPosition = new Vector3();
		anchorSlot.point.getWorldPosition(anchorPosition);

		const movingPosition = new Vector3();
		movingSlot.point.getWorldPosition(movingPosition);

		const offset = new Vector3().subVectors(anchorPosition, movingPosition);
		offset.y = 0;
		this.group.position.add(offset);
	}

	isDeleteable() {
		return (
			this.connectingSlots.filter((slot) => !!slot.connectedSlot).length < 2
		);
	}

	private disposeSlots() {
		for (const slot of this.connectingSlots) {
			slot.dispose();
		}
		this.connectingSlots = [];
	}

	swapModel(asset: Asset, object: Object3D) {
		if (this.modelObject) this.group.remove(this.modelObject);
		this.modelObject = object;
		this.disposeSlots();

		this.asset = structuredClone(asset);
		this.group.add(object);
	}

	dispose() {
		this.disposeSlots();

		RenderUtils.clearGroup(this.group);
		this.group.removeFromParent();
	}

	export(): SerializedSofaModule {
		return {
			id: this.id,
			asset: structuredClone(this.asset),
			materialActions: structuredClone(this.materialActions),
			objectData: {
				position: this.group.position.toArray(),
				rotation: this.group.rotation.toArray(),
			},
			connectingSlots: this.connectingSlots.map((slot) => slot.export()),
		};
	}
}
